import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Coins, Pause, Play as PlayIcon, Target as TargetIcon, Timer,
} from "lucide-react";
import { useGameStore, gameStore } from "@/hooks/useGameStore";
import { audio } from "@/game/audio";
import { haptics } from "@/game/haptics";
import { maybeRequestReview } from "@/game/appReview";
import {
  ARROWS, CROSSBOWS, SKINS, MINI_GAMES, MINIGAME_REWARD, ASSETS,
  UNIQUE_TARGET_IDS, CHAIN_SEQUENCE, CHAIN_MASTER_HITS_PER_LINK,
  TARGETS, SKULL_TARGET, MAX_ARROWS_DEFAULT,
} from "@/data/catalog";
import {
  Arrow, Particle, spawnTarget, spawnTinyFast, spawnZigzag, spawnBossPair,
  spawnBossDropSkull, spawnForHitEmAll, updateBossPhase, applyBossShrink,
  getImage, spawnTrophy, spawnSkull, spawnBalloon, spawnGiant, spawnOfType,
  spawnFrozen, spawnBouncer, hitGround, spawnWallWithTarget,
  spawnChainStart, morphChainTarget, spawnFromAnySide, spawnTrophyTrialSkull,
  spawnArrowPack,
  spawnPinata, spawnPinataBurst, spawnColored, spawnShrinker,
  spawnConveyorLine, spawnMole, spawnFallingCoin,
  spawnMagnet, spawnBoomerang,
  spawnSplitter, spawnMirrorPair, spawnLightning, spawnLuckyWheel,
} from "@/game/engine";

// Modes that run the single morphing chain target.
const CHAIN_MODES = new Set(["target_chain", "chain_master"]);

// Arrow economy — applies to every mode except Trophy Trial (which keeps its
// own 3-arrow challenge rule).
const ARROWS_REPLENISH = 5;
const REPLENISH_INTERVAL_MS = 5_000;
const ARROW_PACK_BONUS = 20;
const ARROW_PACK_SPAWN_MS = 15_000;
const ARROWS_LOW_THRESHOLD = 5;

// Read env(safe-area-inset-bottom) in CSS pixels via a hidden DOM probe.
// Used to keep the drawn crossbow above the iOS home indicator. Returns 0
// on the web / on devices without a bottom inset.
function readSafeAreaBottom() {
  try {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;bottom:0;height:0;padding-bottom:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const v = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    document.body.removeChild(probe);
    return v;
  } catch {
    return 0;
  }
}

// Mutate a freshly spawned target into a skull variant while preserving its
// motion/size so it matches the surrounding non-skull targets in the mode.
function asSkull(target, size) {
  target.def = { ...SKULL_TARGET };
  target.size = size ?? SKULL_TARGET.size;
  return target;
}
import EndOverlay from "@/components/game/EndOverlay";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CLASSIC_DURATION = 45;

export default function PlayScreen({ mode: modeProp }) {
  const params = useParams();
  const mode = modeProp || params.mode || "classic";
  const nav = useNavigate();
  const state = useGameStore();

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);

  const gameRef = useRef({
    w: 0, h: 0,
    mouseX: 0, mouseY: 0,
    targets: [],
    walls: [],
    arrows: [],
    particles: [],
    elapsed: 0,
    paused: false,
    ended: false,
    shakeUntil: 0,
    shakeMag: 0,
  });

  const isMini = mode !== "classic";
  const miniDef = MINI_GAMES.find((m) => m.id === mode);
  const duration = isMini ? miniDef.duration : CLASSIC_DURATION;

  const [score, setScore] = useState(0);
  const [hits, setHits] = useState(0);
  const [timeLeft, setTimeLeft] = useState(duration);
  const [paused, setPaused] = useState(false);
  const [ended, setEnded] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  // Remember whether the player was already paused before the leave dialog
  // opened, so we can restore that state if they cancel. The dialog itself
  // always forces a pause while it's visible.
  const pausedBeforeLeaveRef = useRef(false);
  // Boss Challenge: multiple bosses, each with its own HP / phase / alive flag.
  const [bossHps, setBossHps] = useState([]); // [{ id, hp, max, phase, alive }]
  const bossHpsRef = useRef([]);
  const bossesAliveRef = useRef(0);
  const bossesStartCountRef = useRef(0);
  // "Hit 'Em All" tracking
  const [uniqueHit, setUniqueHit] = useState(() => new Set());
  const uniqueHitRef = useRef(new Set());
  const [scoreZeroed, setScoreZeroed] = useState(false);

  // Arrow economy state — trophy trial keeps its 3-arrow challenge rule,
  // every other mode uses the universal 20-arrow pool.
  const initialArrows = mode === "trophy_trial" ? 3 : MAX_ARROWS_DEFAULT;
  const [arrowsLeft, setArrowsLeft] = useState(initialArrows);
  const arrowsLeftRef = useRef(initialArrows);
  const trophyHitRef = useRef(false);

  // Target Chain state — current index into CHAIN_SEQUENCE. Chain Master
  // also tracks how many times the current link has been hit.
  const chainIndexRef = useRef(0);
  const [chainIndex, setChainIndex] = useState(0);
  const chainHitsOnLinkRef = useRef(0);
  const [chainHitsOnLink, setChainHitsOnLink] = useState(0);

  // Combo Frenzy: streak of consecutive hits with no missed shots.
  const comboRef = useRef(0);
  const [combo, setCombo] = useState(0);
  // Conveyor Combo: in-order multiplier (resets if you skip a target ahead).
  const conveyorMultRef = useRef(1);
  const [conveyorMult, setConveyorMult] = useState(1);

  // Refs for finishRound to read latest values
  const scoreRef = useRef(0);
  const hitsRef = useRef(0);
  // If set, forces finishRound to report a loss regardless of score/hits.
  const lostRef = useRef(false);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    // Cap DPR at 1.5 — retina 2x ran 4x the pixels per frame for a modest
    // quality gain. 1.5 keeps things sharp without tanking slow GPUs.
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    gameRef.current.w = w;
    gameRef.current.h = h;
    // Measure iOS home-indicator inset so we can lift the crossbow above it.
    gameRef.current.safeBottom = readSafeAreaBottom();
  }, []);

  useEffect(() => {
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [resizeCanvas]);

  useEffect(() => {
    audio.unlock();
    const g = gameRef.current;
    // In React StrictMode the effect runs twice; skip if we've already set up.
    if (mode === "boss" && !g.targets.some((t) => t.isBoss)) {
      const bosses = spawnBossPair(g.w, g.h);
      bosses.forEach((b) => g.targets.push(b));
      const list = bosses.map((b) => ({
        id: b.bossId, hp: b.hp, max: b.maxHp, phase: b.phase, alive: true,
      }));
      setBossHps(list);
      bossHpsRef.current = list;
      bossesAliveRef.current = bosses.length;
      bossesStartCountRef.current = bosses.length;
    }
    if (mode === "trophy_trial" && !g.targets.some((t) => t.def.difficulty === "trophy")) {
      const trophy = spawnTrophy(g.w, g.h);
      g.targets.push(trophy);
      // Seed 3 skulls clustered right around the trophy from frame 1 so the
      // player never gets a free shot before skulls are on screen.
      for (let i = 0; i < 3; i++) {
        g.targets.push(spawnTrophyTrialSkull(g.w, g.h, trophy));
      }
    }
    if (CHAIN_MODES.has(mode) && !g.targets.some((t) => t.isChain)) {
      const firstDef = TARGETS.find((t) => t.id === CHAIN_SEQUENCE[0]);
      if (firstDef) {
        const t = spawnChainStart(g.w, g.h, firstDef);
        g.targets.push(t);
      }
      chainIndexRef.current = 0;
      chainHitsOnLinkRef.current = 0;
      setChainIndex(0);
      setChainHitsOnLink(0);
    }
    // Piñata Party: seed the first piñata so the player doesn't stare at an
    // empty arena while the first respawn timer ticks.
    if (mode === "pinata_party" && !g.targets.some((t) => t.isPinata)) {
      g.targets.push(spawnPinata(g.w, g.h));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  function syncBossHps() {
    const g = gameRef.current;
    const activeBosses = g.targets.filter((t) => t.isBoss);
    const next = bossHpsRef.current.map((b) => {
      const found = activeBosses.find((x) => x.bossId === b.id);
      if (!found) return { ...b, hp: 0, alive: false };
      return { ...b, hp: Math.max(0, found.hp), phase: found.phase, alive: true };
    });
    bossHpsRef.current = next;
    setBossHps(next);
  }

  useEffect(() => {
    if (ended || paused) return;
    if (timeLeft <= 0) { finishRound(); return; }
    const t = setTimeout(() => {
      setTimeLeft((s) => s - 1);
      if (timeLeft <= 5) {
        audio.countdown();
        haptics.warning();
      }
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, paused, ended]);

  // Replenish +5 arrows 10s AFTER the player runs out (all modes except
  // trophy_trial). A short ticker keeps the on-screen countdown fresh.
  const emptyAtRef = useRef(null);
  const [refillIn, setRefillIn] = useState(null); // seconds until next refill

  useEffect(() => {
    if (mode === "trophy_trial") return;
    const id = setInterval(() => {
      const g = gameRef.current;
      if (g.ended) return;
      if (arrowsLeftRef.current > 0) {
        if (emptyAtRef.current !== null) emptyAtRef.current = null;
        if (refillIn !== null) setRefillIn(null);
        return;
      }
      // Arrows are 0. Start / resume the countdown (pausing when paused).
      if (g.paused) return;
      if (emptyAtRef.current === null) {
        emptyAtRef.current = Date.now();
      }
      const elapsed = Date.now() - emptyAtRef.current;
      const remaining = REPLENISH_INTERVAL_MS - elapsed;
      if (remaining <= 0) {
        arrowsLeftRef.current += ARROWS_REPLENISH;
        setArrowsLeft(arrowsLeftRef.current);
        audio.coin();
        if (arrowsLeftRef.current <= 0) {
          // Shouldn't happen (+5 from 0), but stay safe: keep countdown.
          emptyAtRef.current = Date.now();
          setRefillIn(Math.ceil(REPLENISH_INTERVAL_MS / 1000));
        } else {
          emptyAtRef.current = null;
          setRefillIn(null);
        }
      } else {
        const secs = Math.ceil(remaining / 1000);
        if (secs !== refillIn) setRefillIn(secs);
      }
    }, 200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, refillIn]);

  function finishRound() {
    if (gameRef.current.ended) return;
    gameRef.current.ended = true;
    gameStore.incrementGamesPlayed();
    const sc = scoreRef.current;
    let win = false;
    if (lostRef.current) win = false;
    else if (mode === "classic") win = sc >= 200;
    else if (mode === "boss") {
      // Require every boss we started with to be defeated.
      win = bossesStartCountRef.current > 0 && bossesAliveRef.current <= 0;
    }
    else if (mode === "hit_em_all") {
      win = uniqueHitRef.current.size >= UNIQUE_TARGET_IDS.length && sc > 0;
    }
    else if (mode === "trophy_trial") win = trophyHitRef.current;
    else win = hitsRef.current >= miniDef.hitGoal;

    if (win) {
      audio.victory();
      const reward = isMini ? MINIGAME_REWARD : 0;
      if (reward) gameStore.addCoins(reward);
      if (isMini) gameStore.markMiniGameWon(mode);
      if (mode === "classic" && sc >= 300) gameStore.setLevel(state.level + 1);
      gameStore.setBestScore(sc);
      setEnded("win");
    } else {
      audio.defeat();
      setEnded("lose");
    }
    // Give the end-of-round UI a moment to settle before asking for a review.
    setTimeout(() => { maybeRequestReview(); }, 1500);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let lastSpawn = 0;
    let lastBossDrop = 0;
    let lastPackSpawn = 0;
    const BOSS_DROP_INTERVAL = 3200; // ms between each boss's skull drops
    const spawnRate = (() => {
      if (mode === "tiny_fast") return 500;
      if (mode === "zigzag") return 700;
      if (mode === "rain") return 220;
      if (mode === "hit_em_all") return 900;
      if (mode === "boss") return 99999;
      if (mode === "trophy_trial") return 2200;
      if (mode === "balloon_pop") return 600;
      if (mode === "speed_demon") return 180;
      if (mode === "giants") return 1400;
      if (mode === "swarm") return 220;
      if (mode === "skull_dodge") return 500;
      if (mode === "frozen") return 800;
      if (mode === "rocket_rush") return 450;
      if (mode === "bounce_house") return 700;
      if (mode === "wall_breaker") return 2400;
      if (CHAIN_MODES.has(mode)) return 99999;
      // Kid-friendly chaos batch
      if (mode === "pinata_party") return 99999;     // piñata + bursts handle their own pacing
      if (mode === "combo_frenzy") return 280;       // dense, tight rhythm
      if (mode === "color_match") return 500;
      if (mode === "shrinking_stars") return 750;
      if (mode === "firework_frenzy") return 600;
      if (mode === "bullseye") return 700;
      if (mode === "conveyor_combo") return 4500;    // a 6-target line every ~4.5s
      if (mode === "mole_mash") return 500;
      if (mode === "triple_shot") return 320;
      if (mode === "coin_storm") return 280;
      if (mode === "magnet_pull") return 700;
      if (mode === "hot_potato") return 600;
      if (mode === "bouncy_arrows") return 600;
      if (mode === "boomerang") return 700;
      if (mode === "floor_lava") return 500;
      if (mode === "splitter") return 1500;       // each spawn becomes many
      if (mode === "mirror_twins") return 1200;   // each spawn is a pair
      if (mode === "time_bandit") return 500;
      if (mode === "slowmo") return 600;
      if (mode === "lucky_wheel") return 700;
      return 900 - Math.min(500, state.level * 60);
    })();
    let lastPinataAt = 0;
    const PINATA_RESPAWN_MS = 8000;
    let lastMagnetAt = 0;
    const MAGNET_RESPAWN_MS = 5000;
    let lastLightningAt = 0;
    const LIGHTNING_RESPAWN_MS = 5000;
    let lastWheelAt = 0;
    const WHEEL_RESPAWN_MS = 8000;
    const MAX_WALLS = 3;

    const loop = (ts) => {
      const g = gameRef.current;
      if (g.ended) return;

      if (!g.paused) {
        // Trophy Trial: always keep the trophy on screen
        if (mode === "trophy_trial" && !trophyHitRef.current && !g.targets.some((t) => t.def.difficulty === "trophy")) {
          g.targets.push(spawnTrophy(g.w, g.h));
        }

        if (ts - lastSpawn > spawnRate) {
          lastSpawn = ts;
          if (mode === "tiny_fast") {
            const t = spawnTinyFast(g.w, g.h);
            if (Math.random() < 0.2) asSkull(t, 50);
            g.targets.push(t);
          }
          else if (mode === "zigzag") g.targets.push(spawnZigzag(g.w, g.h));
          else if (mode === "rain") g.targets.push(spawnTarget(g.w, g.h, 2, "rain"));
          else if (mode === "hit_em_all") {
            if (Math.random() < 0.15) g.targets.push(spawnSkull(g.w, g.h, 1));
            else g.targets.push(spawnForHitEmAll(g.w, g.h, uniqueHitRef.current));
          }
          else if (mode === "classic") g.targets.push(spawnTarget(g.w, g.h, state.level));
          else if (mode === "trophy_trial") {
            const trophy = g.targets.find((t) => t.def.difficulty === "trophy");
            const skullCount = g.targets.filter((t) => t.def.difficulty === "skull").length;
            if (skullCount < 3) g.targets.push(spawnTrophyTrialSkull(g.w, g.h, trophy));
          }
          else if (mode === "balloon_pop") {
            const t = spawnBalloon(g.w, g.h);
            if (Math.random() < 0.2) asSkull(t);
            g.targets.push(t);
          }
          else if (mode === "speed_demon") g.targets.push(spawnFromAnySide(g.w, g.h, 3));
          else if (mode === "giants") {
            const t = spawnGiant(g.w, g.h);
            if (Math.random() < 0.22) asSkull(t, 160);
            g.targets.push(t);
          }
          else if (mode === "swarm") g.targets.push(spawnTinyFast(g.w, g.h));
          else if (mode === "skull_dodge") {
            g.targets.push(Math.random() < 0.55 ? spawnSkull(g.w, g.h, 2) : spawnTarget(g.w, g.h, 1));
          }
          else if (mode === "frozen") g.targets.push(spawnFrozen(g.w, g.h));
          else if (mode === "rocket_rush") g.targets.push(spawnOfType(g.w, g.h, "target_hotdog_rocket"));
          else if (mode === "bounce_house") g.targets.push(spawnBouncer(g.w, g.h));
          else if (mode === "wall_breaker") {
            if (g.walls.length < MAX_WALLS) {
              const { wall, target } = spawnWallWithTarget(g.w, g.h);
              g.walls.push(wall);
              g.targets.push(target);
            }
          }
          else if (mode === "combo_frenzy") g.targets.push(spawnTarget(g.w, g.h, 3));
          else if (mode === "color_match") g.targets.push(spawnColored(g.w, g.h));
          else if (mode === "shrinking_stars") g.targets.push(spawnShrinker(g.w, g.h));
          else if (mode === "firework_frenzy") g.targets.push(spawnTarget(g.w, g.h, 2));
          else if (mode === "bullseye") g.targets.push(spawnTarget(g.w, g.h, 2));
          else if (mode === "conveyor_combo") {
            spawnConveyorLine(g.w, g.h).forEach((t) => g.targets.push(t));
          }
          else if (mode === "mole_mash") g.targets.push(spawnMole(g.w, g.h));
          else if (mode === "triple_shot") g.targets.push(spawnTarget(g.w, g.h, 3));
          else if (mode === "coin_storm") {
            // Mostly coins, with regular falling targets to count toward the goal.
            g.targets.push(Math.random() < 0.6
              ? spawnFallingCoin(g.w, g.h)
              : spawnTarget(g.w, g.h, 2, "rain"));
          }
          else if (mode === "magnet_pull") g.targets.push(spawnTarget(g.w, g.h, 2));
          else if (mode === "hot_potato") {
            // Apply a 3-second fuse to whatever spawns: the engine's lifespan
            // path auto-kills it if the player doesn't shoot in time.
            const t = spawnTarget(g.w, g.h, 2);
            t.lifespan = 180;
            t.def.lifespan = 180;
            g.targets.push(t);
          }
          else if (mode === "bouncy_arrows") g.targets.push(spawnTarget(g.w, g.h, 2));
          else if (mode === "boomerang") g.targets.push(spawnBoomerang(g.w, g.h));
          else if (mode === "floor_lava") g.targets.push(spawnTarget(g.w, g.h, 2, "rain"));
          else if (mode === "splitter") g.targets.push(spawnSplitter(g.w, g.h, 0));
          else if (mode === "mirror_twins") {
            spawnMirrorPair(g.w, g.h).forEach((t) => g.targets.push(t));
          }
          else if (mode === "time_bandit") g.targets.push(spawnTarget(g.w, g.h, 2));
          else if (mode === "slowmo") g.targets.push(spawnTarget(g.w, g.h, 2));
          else if (mode === "lucky_wheel") g.targets.push(spawnTarget(g.w, g.h, 2));
        }

        // Piñata Party: respawn the piñata on its own cooldown so the bursts
        // overlap nicely with the next container.
        if (mode === "pinata_party" && !g.targets.some((t) => t.isPinata)
            && ts - lastPinataAt > PINATA_RESPAWN_MS) {
          lastPinataAt = ts;
          g.targets.push(spawnPinata(g.w, g.h));
        }

        // Magnet Pull: every ~5s drop in a magnet (when none alive). While it
        // lives, nudge nearby targets toward it so a cluster forms; hitting
        // the magnet chain-pops everything clustered.
        if (mode === "magnet_pull") {
          if (!g.targets.some((t) => t.isMagnet) && ts - lastMagnetAt > MAGNET_RESPAWN_MS) {
            lastMagnetAt = ts;
            g.targets.push(spawnMagnet(g.w, g.h));
          }
          const magnets = g.targets.filter((t) => t.isMagnet && !t.dead);
          if (magnets.length > 0) {
            const PULL_RADIUS = 280;
            const PULL_FORCE = 0.28;
            g.targets.forEach((t) => {
              if (t.isMagnet || t.dead || t.isCoinPickup || t.isBoss) return;
              magnets.forEach((m) => {
                const dx = m.x - t.x, dy = m.y - t.y;
                const d = Math.hypot(dx, dy);
                if (d < 1 || d > PULL_RADIUS) return;
                t.vx += (dx / d) * PULL_FORCE;
                t.vy += (dy / d) * PULL_FORCE;
              });
            });
          }
        }

        // Slow-Mo Drop: drop a lightning pickup periodically.
        if (mode === "slowmo"
            && !g.targets.some((t) => t.isLightning)
            && ts - lastLightningAt > LIGHTNING_RESPAWN_MS) {
          lastLightningAt = ts;
          g.targets.push(spawnLightning(g.w, g.h));
        }

        // Lucky Wheel: drop the prize wheel periodically.
        if (mode === "lucky_wheel"
            && !g.targets.some((t) => t.isLuckyWheel)
            && ts - lastWheelAt > WHEEL_RESPAWN_MS) {
          lastWheelAt = ts;
          g.targets.push(spawnLuckyWheel(g.w, g.h));
        }

        // Boomerang: when an outbound target crosses the far edge, reverse vx
        // and flag isReturning so the onHit applies the 3x reward.
        if (mode === "boomerang") {
          g.targets.forEach((t) => {
            if (!t.isBoomerang || t.hasReversed) return;
            const farLeft = -40, farRight = g.w + 40;
            if ((t.fromLeft && t.x >= farRight) || (!t.fromLeft && t.x <= farLeft)) {
              t.vx = -t.vx;
              t.hasReversed = true;
              t.isReturning = true;
              t.tintGlow = "#39FF14"; // green ring on the way back = "return!"
            }
          });
        }

        // Arrow Pack pickup spawns (every mode except trophy_trial, which
        // is a 3-arrow challenge on purpose).
        if (mode !== "trophy_trial" && ts - lastPackSpawn > ARROW_PACK_SPAWN_MS) {
          lastPackSpawn = ts;
          g.targets.push(spawnArrowPack(g.w, g.h));
        }

        // Boss Challenge: each boss periodically drops a skull that falls
        // toward the ground. Shoot it or it regens the boss.
        if (mode === "boss" && ts - lastBossDrop > BOSS_DROP_INTERVAL) {
          lastBossDrop = ts;
          g.targets
            .filter((t) => t.isBoss && t.hp > 0)
            .forEach((boss) => g.targets.push(spawnBossDropSkull(boss)));
        }

        g.walls.forEach((w) => w.update(g.w, g.h));
        // Slow-Mo Drop: while bullet time is active, run target + arrow
        // physics on alternate frames only (≈half speed). Particles stay
        // full-speed so explosions still feel snappy.
        const inSlowMo = g.slowMoUntil && ts < g.slowMoUntil;
        g.physicsFrame = (g.physicsFrame || 0) + 1;
        const skipPhysics = inSlowMo && g.physicsFrame % 2 === 0;
        if (!skipPhysics) {
          g.targets.forEach((t) => {
            // Hidden targets ride their wall — skip their normal motion.
            if (t.hiddenBy && !t.hiddenBy.dead) return;
            t.update(g.w, g.h);
          });
          g.arrows.forEach((a) => { a.update(); a.tryBounce(g.w, g.h); });
        }
        g.particles.forEach((p) => p.update());

        for (let i = g.arrows.length - 1; i >= 0; i--) {
          const a = g.arrows[i];
          let hit = false;
          // Walls get first crack at the arrow — they shield anything behind.
          for (let w = g.walls.length - 1; w >= 0; w--) {
            const wall = g.walls[w];
            if (!wall.dead && wall.hitTest(a.x, a.y)) {
              hit = true;
              onWallHit(wall, a.x, a.y);
              g.arrows.splice(i, 1);
              break;
            }
          }
          if (hit) continue;
          for (let j = g.targets.length - 1; j >= 0; j--) {
            const t = g.targets[j];
            // Hidden targets are untouchable until their wall breaks.
            if (t.hiddenBy && !t.hiddenBy.dead) continue;
            if (t.hitTest(a.x, a.y)) {
              hit = true;
              onHit(t, a.x, a.y, a);
              g.arrows.splice(i, 1);
              break;
            }
          }
          if (!hit && a.offScreen(g.w, g.h)) {
            // Combo Frenzy: an arrow that left the arena without connecting
            // breaks the streak.
            if (mode === "combo_frenzy" && comboRef.current > 0) {
              comboRef.current = 0;
              setCombo(0);
              g.particles.push(new Particle(a.x, Math.min(g.h - 40, Math.max(40, a.y)), "#FF3B30", {
                text: "MISS!", life: 35, gravity: 0.05, vy: -1.4, vx: 0,
              }));
            }
            g.arrows.splice(i, 1);
          }
        }

        for (let i = g.targets.length - 1; i >= 0; i--) {
          const t = g.targets[i];
          if (t.dead) { g.targets.splice(i, 1); continue; }
          // Boss-drop skull reached the floor → heal its parent boss.
          if (t.isBossDrop && hitGround(t, g.h)) {
            const parent = g.targets.find((x) => x.isBoss && x.bossId === t.parentBossId);
            if (parent && parent.hp > 0) {
              parent.hp = Math.min(parent.maxHp, parent.hp + 2);
              applyBossShrink(parent);
              syncBossHps();
              // visual warning: red splash + shake
              explode(t.x, g.h - 8, 24, "#FF3B30");
              g.particles.push(new Particle(t.x, g.h - 40, "#FF3B30", {
                text: "+2 HP BOSS!", life: 60, gravity: 0.04, vy: -1.8, vx: 0,
              }));
              triggerShake(260, 14);
              audio.defeat();
            }
            g.targets.splice(i, 1);
            continue;
          }
          // Rain mode: targets that reach the floor explode in place instead
          // of bouncing back up or silently leaving.
          if (mode === "rain" && !t.isBoss && t.y + t.size / 2 >= g.h - 6) {
            explode(t.x, g.h - 8, 22, "#FFD700");
            explode(t.x, g.h - 8, 10, "#FF007F");
            g.targets.splice(i, 1);
            continue;
          }
          // Floor Is Lava: a target that reaches the ground costs 10 coins
          // and pops in red. Doesn't end the round, just ratchets the urgency.
          if (mode === "floor_lava" && !t.isBoss && t.y + t.size / 2 >= g.h - 6) {
            const penalty = -10;
            gameStore.addCoins(penalty);
            setScore((s) => {
              const next = Math.max(0, s + penalty);
              scoreRef.current = next;
              return next;
            });
            audio.miss();
            haptics.warning();
            explode(t.x, g.h - 8, 22, "#FF3B30");
            explode(t.x, g.h - 8, 10, "#FF6A00");
            g.particles.push(new Particle(t.x, g.h - 30, "#FF3B30", {
              text: `${penalty}`, life: 45, gravity: 0.08, vy: -1.6, vx: 0,
            }));
            triggerShake(120, 6);
            g.targets.splice(i, 1);
            continue;
          }
          if (!t.isBoss && t.offScreen(g.w, g.h)) g.targets.splice(i, 1);
        }

        // Remove destroyed walls.
        for (let i = g.walls.length - 1; i >= 0; i--) {
          if (g.walls[i].dead) g.walls.splice(i, 1);
        }

        g.particles = g.particles.filter((p) => !p.dead);
      }

      // Screen shake transform
      let shakeX = 0, shakeY = 0;
      if (ts < g.shakeUntil) {
        shakeX = (Math.random() - 0.5) * g.shakeMag;
        shakeY = (Math.random() - 0.5) * g.shakeMag;
      }

      ctx.save();
      ctx.clearRect(0, 0, g.w, g.h);
      ctx.translate(shakeX, shakeY);

      // floor gradient
      const grd = ctx.createLinearGradient(0, g.h - 120, 0, g.h);
      grd.addColorStop(0, "rgba(0,255,255,0)");
      grd.addColorStop(1, "rgba(0,255,255,0.15)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, g.h - 120, g.w, 120);

      g.targets.forEach((t) => t.draw(ctx));
      // Walls render after targets so hidden targets peek out from behind.
      g.walls.forEach((w) => w.draw(ctx));
      g.arrows.forEach((a) => a.draw(ctx));
      g.particles.forEach((p) => p.draw(ctx));

      drawCrosshair(ctx, g.mouseX, g.mouseY);
      drawCrossbow(ctx, g);
      ctx.restore();

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state.level]);

  function triggerShake(ms = 200, mag = 10) {
    gameRef.current.shakeUntil = performance.now() + ms;
    gameRef.current.shakeMag = mag;
  }

  function onWallHit(wall, x, y) {
    const g = gameRef.current;
    wall.hp -= 1;
    audio.hit("easy");
    const pct = Math.max(0, wall.hp) / wall.maxHp;
    const color = pct > 0.66 ? "#39FF14" : pct > 0.33 ? "#FFD700" : "#FF3B30";
    explode(x, y, 10, color);
    triggerShake(80, 4);
    if (wall.hp <= 0) {
      wall.dead = true;
      // blow up the bricks
      explode(wall.x, wall.y, 40, "#FF6A00");
      explode(wall.x, wall.y, 25, "#FFD700");
      triggerShake(220, 12);
      audio.funny();
      // release the hidden target so it can drift freely
      if (wall.childTarget && !wall.childTarget.dead) {
        const t = wall.childTarget;
        t.hiddenBy = null;
        t.vx = wall.vx * 1.6;
        t.vy = -2;
        t.motion = t.def.motion || "linear";
        t.baseY = t.y;
      }
      g.particles.push(new Particle(wall.x, wall.y - 30, "#FFD700", {
        text: "BREAK!", life: 50, gravity: 0.05, vy: -2, vx: 0,
      }));
    }
  }

  function onHit(target, x, y, arrow) {
    const g = gameRef.current;
    let coin = target.def.coin;
    if (mode !== "classic") coin = Math.max(5, Math.floor(Math.abs(coin) / 2)) * Math.sign(coin || 1);

    // Target Chain / Chain Master: redirect the target along the arrow's
    // direction. In target_chain we morph to the next link on every hit;
    // in chain_master it takes CHAIN_MASTER_HITS_PER_LINK hits per link.
    if (CHAIN_MODES.has(mode) && target.isChain) {
      const hitsPerLink = mode === "chain_master" ? CHAIN_MASTER_HITS_PER_LINK : 1;
      const linkIdx = chainIndexRef.current;

      // Combine the target's current trajectory with the arrow's impulse
      // (like a billiard ball getting kicked) — don't just snap onto the
      // arrow's direction. Cap the resulting speed so it stays playable.
      if (arrow) {
        const aSp = Math.hypot(arrow.vx, arrow.vy) || 1;
        const impulse = 2.8;
        target.vx += (arrow.vx / aSp) * impulse;
        target.vy += (arrow.vy / aSp) * impulse;
        const MAX_SPEED = 8;
        const tSp = Math.hypot(target.vx, target.vy);
        if (tSp > MAX_SPEED) {
          target.vx = (target.vx / tSp) * MAX_SPEED;
          target.vy = (target.vy / tSp) * MAX_SPEED;
        }
        target.baseY = target.y;
      }

      audio.hit(target.def.difficulty);
      audio.coin();
      const gained = mode === "chain_master" ? 40 : 60;
      gameStore.addCoins(gained);
      setScore((s) => { scoreRef.current = s + gained; return s + gained; });
      setHits((h) => { hitsRef.current = h + 1; return h + 1; });
      g.particles.push(new Particle(x, y - 18, "#00FFFF", {
        text: `+${gained}`, life: 40, gravity: 0.08, vy: -1.8, vx: 0,
      }));
      explode(x, y, 18, "#00FFFF");
      triggerShake(100, 5);

      const linkHits = chainHitsOnLinkRef.current + 1;
      const morph = linkHits >= hitsPerLink;

      if (!morph) {
        // partial link progress — just show progress pip feedback
        chainHitsOnLinkRef.current = linkHits;
        setChainHitsOnLink(linkHits);
        g.particles.push(new Particle(x, y - 36, "#B366FF", {
          text: `${linkHits}/${hitsPerLink}`, life: 35, gravity: 0.06, vy: -1.3, vx: 0,
        }));
        return;
      }

      // Morph: reset link hits, bump index, swap def.
      chainHitsOnLinkRef.current = 0;
      setChainHitsOnLink(0);
      const nextIdx = linkIdx + 1;
      chainIndexRef.current = nextIdx;
      setChainIndex(nextIdx);
      explode(x, y, 22, "#FFD700");

      if (nextIdx >= CHAIN_SEQUENCE.length) {
        // last link cleared — big finish
        target.dead = true;
        explode(x, y, 36, "#FFD700");
        explode(x, y, 24, "#FF007F");
        triggerShake(320, 14);
        g.particles.push(new Particle(x, y - 30, "#FFD700", {
          text: "CHAIN COMPLETE!", life: 70, gravity: 0.03, vy: -2, vx: 0,
        }));
        setTimeout(() => finishRound(), 700);
      } else {
        const nextDef = TARGETS.find((t) => t.id === CHAIN_SEQUENCE[nextIdx]);
        if (nextDef) morphChainTarget(target, nextDef);
      }
      return;
    }

    // Trophy Trial: hitting the trophy = win
    if (mode === "trophy_trial" && target.def.difficulty === "trophy") {
      trophyHitRef.current = true;
      target.dead = true;
      audio.funny();
      audio.coin();
      explode(x, y, 40, "#FFD700");
      explode(x, y, 20, "#FF007F");
      g.particles.push(new Particle(x, y - 20, "#FFD700", {
        text: "TROPHY!", life: 60, gravity: 0.05, vy: -2, vx: 0,
      }));
      triggerShake(300, 12);
      setTimeout(() => finishRound(), 800);
      return;
    }

    // Trophy Trial: hitting a skull = instant loss, no prize
    if (mode === "trophy_trial" && target.def.difficulty === "skull") {
      if (trophyHitRef.current) return; // already won, ignore
      target.dead = true;
      audio.defeat();
      audio.miss();
      explode(x, y, 22, "#FF3B30");
      g.particles.push(new Particle(x, y - 20, "#FF3B30", {
        text: "NO PRIZE!", life: 55, gravity: 0.05, vy: -2, vx: 0,
      }));
      triggerShake(300, 16);
      setTimeout(() => finishRound(), 600);
      return;
    }

    // Splitter: hit a big target → it spawns 2 smaller, faster copies until
    // generation 2 (final). Coin reward grows with each generation.
    if (target.isSplitter) {
      target.dead = true;
      const SPLIT_COINS = [10, 25, 75];
      const reward = SPLIT_COINS[target.splitGen] || 10;
      audio.hit(target.splitGen >= 2 ? "hard" : "medium");
      haptics.light();
      gameStore.addCoins(reward);
      setScore((s) => { scoreRef.current = s + reward; return s + reward; });
      setHits((h) => { hitsRef.current = h + 1; return h + 1; });
      explode(x, y, 14, "#FF007F");
      g.particles.push(new Particle(x, y - 18, "#FFD700", {
        text: `+${reward}`, life: 40, gravity: 0.1, vy: -2, vx: 0,
      }));
      if (target.splitGen < 2) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + target.splitGen;
        for (let s = -1; s <= 1; s += 2) {
          const child = spawnSplitter(g.w, g.h, target.splitGen + 1);
          child.x = x; child.y = y; child.baseY = y;
          child.vx = Math.cos(angle) * speed * s;
          child.vy = Math.sin(angle) * speed * s;
          child.hasEntered = true;
          g.targets.push(child);
        }
      } else {
        // Smallest tier popped — extra confetti for the jackpot moment.
        explode(x, y, 18, "#FFD700");
        triggerShake(120, 6);
      }
      return;
    }

    // Slow-Mo Drop lightning: trigger 3 seconds of bullet time. Doesn't
    // count toward hits-vs-goal — pure utility pickup.
    if (target.isLightning) {
      target.dead = true;
      g.slowMoUntil = performance.now() + 3000;
      audio.funny();
      haptics.medium();
      explode(x, y, 28, "#00FFFF");
      explode(x, y, 18, "#FFFFFF");
      triggerShake(220, 10);
      g.particles.push(new Particle(x, y - 32, "#00FFFF", {
        text: "SLOW-MO!", life: 60, gravity: 0.04, vy: -2, vx: 0,
      }));
      return;
    }

    // Lucky Wheel: random reward. Doesn't count toward hits — purely a buff.
    if (target.isLuckyWheel) {
      target.dead = true;
      const rewards = ["arrows", "time", "coins", "slowmo"];
      const pick = rewards[Math.floor(Math.random() * rewards.length)];
      let label = "";
      if (pick === "arrows") {
        arrowsLeftRef.current += 10;
        setArrowsLeft(arrowsLeftRef.current);
        label = "+10 ARROWS!";
      } else if (pick === "time") {
        setTimeLeft((t) => t + 5);
        label = "+5s TIMER!";
      } else if (pick === "coins") {
        gameStore.addCoins(200);
        scoreRef.current += 200;
        setScore(scoreRef.current);
        label = "+200 COINS!";
      } else {
        g.slowMoUntil = performance.now() + 3000;
        label = "SLOW-MO!";
      }
      audio.funny();
      haptics.medium();
      explode(x, y, 32, "#FFD700");
      explode(x, y, 22, "#FF007F");
      triggerShake(240, 11);
      g.particles.push(new Particle(x, y - 32, "#FFD700", {
        text: label, life: 70, gravity: 0.04, vy: -2, vx: 0,
      }));
      return;
    }

    // Magnet Pull: hitting the magnet chain-pops every clustered target
    // around it. The magnet itself counts as one hit.
    if (target.isMagnet) {
      target.dead = true;
      audio.funny();
      haptics.medium();
      const RADIUS = 200;
      const cluster = g.targets.filter((t) =>
        !t.dead && t !== target && !t.isMagnet && !t.isCoinPickup && !t.isBoss
        && t.def.difficulty !== "skull"
        && Math.hypot(t.x - target.x, t.y - target.y) <= RADIUS
      );
      let chainCoins = 0;
      cluster.forEach((t) => {
        t.dead = true;
        explode(t.x, t.y, 12, "#A020F0");
        chainCoins += t.def.coin || 0;
      });
      gameStore.addCoins(chainCoins);
      scoreRef.current += chainCoins;
      hitsRef.current += cluster.length + 1;
      setScore(scoreRef.current);
      setHits(hitsRef.current);
      triggerShake(260, 12);
      explode(x, y, 32, "#A020F0");
      explode(x, y, 18, "#FFD700");
      g.particles.push(new Particle(x, y - 32, "#A020F0", {
        text: `MAGNET POP x${cluster.length + 1}!`, life: 60, gravity: 0.04, vy: -2, vx: 0,
      }));
      return;
    }

    // Piñata Party: multi-hit container; on death bursts into candy targets.
    if (target.isPinata) {
      target.hp -= 1;
      audio.hit("hard");
      haptics.medium();
      explode(x, y, 14, "#FF69B4");
      explode(x, y, 8, "#FFD700");
      triggerShake(120, 6);
      if (target.hp <= 0) {
        target.dead = true;
        audio.funny();
        explode(x, y, 32, "#FFD700");
        explode(x, y, 22, "#FF007F");
        triggerShake(280, 12);
        spawnPinataBurst(x, y, 8).forEach((t) => g.targets.push(t));
        g.particles.push(new Particle(x, y - 28, "#FFD700", {
          text: "CANDY BURST!", life: 55, gravity: 0.04, vy: -2, vx: 0,
        }));
      }
      return;
    }

    // Coin Storm pickup: +50 coins, no penalty, counts toward the hit goal
    // (anything you successfully shoot in this mode is progress).
    if (target.def.difficulty === "coin_pickup") {
      target.dead = true;
      const reward = target.def.coin || 50;
      gameStore.addCoins(reward);
      setScore((s) => { scoreRef.current = s + reward; return s + reward; });
      setHits((h) => { hitsRef.current = h + 1; return h + 1; });
      audio.coin();
      haptics.light();
      explode(x, y, 16, "#FFD700");
      g.particles.push(new Particle(x, y - 18, "#FFD700", {
        text: `+${reward}`, life: 40, gravity: 0.1, vy: -2, vx: 0,
      }));
      return;
    }

    // Color Match: hitting a red target = small penalty, no streak/hit credit.
    if (mode === "color_match" && target.colorState === "red") {
      target.dead = true;
      audio.miss();
      haptics.warning();
      explode(x, y, 20, "#FF3B30");
      g.particles.push(new Particle(x, y - 18, "#FF3B30", {
        text: "WRONG COLOR!", life: 50, gravity: 0.05, vy: -1.6, vx: 0,
      }));
      triggerShake(180, 8);
      const penalty = -20;
      gameStore.addCoins(penalty);
      setScore((s) => {
        const next = Math.max(0, s + penalty);
        scoreRef.current = next;
        return next;
      });
      return;
    }

    // Arrow Pack pickup — always a refill, never a penalty.
    if (target.def.difficulty === "pack") {
      target.dead = true;
      arrowsLeftRef.current += ARROW_PACK_BONUS;
      setArrowsLeft(arrowsLeftRef.current);
      audio.buy();
      haptics.medium();
      explode(x, y, 32, "#00FFFF");
      explode(x, y, 16, "#FFD700");
      g.particles.push(new Particle(x, y - 22, "#00FFFF", {
        text: `+${ARROW_PACK_BONUS} ARROWS!`, life: 60, gravity: 0.04, vy: -2, vx: 0,
      }));
      triggerShake(160, 8);
      return;
    }

    // Skull penalty
    if (target.def.difficulty === "skull") {
      target.dead = true;
      // Boss-drop skulls: intercepting is GOOD — tiny reward, no penalty.
      if (target.isBossDrop) {
        audio.coin();
        explode(x, y, 18, "#FFD700");
        g.particles.push(new Particle(x, y - 20, "#FFD700", {
          text: "INTERCEPTED!", life: 45, gravity: 0.08, vy: -1.6, vx: 0,
        }));
        return;
      }
      audio.defeat();
      audio.miss();
      haptics.error();
      // Skull Dodge: touching a skull ends the round instantly as a loss.
      if (mode === "skull_dodge") {
        lostRef.current = true;
        explode(x, y, 30, "#FF3B30");
        explode(x, y, 14, "#FFFFFF");
        g.particles.push(new Particle(x, y - 20, "#FF3B30", {
          text: "HIT A SKULL!", life: 60, gravity: 0.05, vy: -2, vx: 0,
        }));
        triggerShake(420, 18);
        setTimeout(() => finishRound(), 700);
        return;
      }
      // Hit 'Em All: hitting a skull wipes the unique-target progress.
      if (mode === "hit_em_all") {
        uniqueHitRef.current = new Set();
        setUniqueHit(new Set());
        setScore(0);
        scoreRef.current = 0;
        setScoreZeroed(true);
        setTimeout(() => setScoreZeroed(false), 700);
        g.particles.push(new Particle(x, y - 20, "#FF3B30", {
          text: "SKULL! RESET!", life: 55, gravity: 0.05, vy: -2, vx: 0,
        }));
        explode(x, y, 26, "#FF3B30");
        triggerShake(300, 16);
        return;
      }
      const penalty = -30;
      gameStore.addCoins(penalty); // addCoins floors at 0
      setScore((s) => {
        const next = Math.max(0, s + penalty);
        scoreRef.current = next;
        return next;
      });
      g.particles.push(new Particle(x, y - 20, "#FF3B30", {
        text: `${penalty}`, life: 45, gravity: 0.1, vy: -2, vx: 0,
      }));
      explode(x, y, 22, "#FF3B30");
      triggerShake(220, 14);
      return;
    }

    if (target.isBoss) {
      target.hp -= 1;
      applyBossShrink(target);
      updateBossPhase(target);
      audio.hit(target.def.difficulty);
      triggerShake(target.phase >= 3 ? 240 : 150, 8 + target.phase * 3);
      for (let i = 0; i < 10; i++) {
        g.particles.push(new Particle(x, y, "#FFD700", { size: 3, life: 25 }));
      }
      // small coin reward per boss hit
      const bossCoin = 20;
      gameStore.addCoins(bossCoin);
      setScore((s) => { scoreRef.current = s + bossCoin; return s + bossCoin; });
      g.particles.push(new Particle(x, y - 16, "#FFD700", {
        text: `+${bossCoin}`, life: 35, gravity: 0.12, vy: -2, vx: 0,
      }));
      if (target.hp <= 0) {
        target.dead = true;
        bossesAliveRef.current -= 1;
        audio.funny();
        explode(x, y, 40, "#FF007F");
        explode(x, y, 30, "#FFD700");
        triggerShake(450, 20);
        g.particles.push(new Particle(x, y - 30, "#39FF14", {
          text: bossesAliveRef.current > 0 ? "1 DOWN!" : "BOTH DOWN!",
          life: 70, gravity: 0.04, vy: -2, vx: 0,
        }));
        syncBossHps();
        if (bossesAliveRef.current <= 0) {
          setTimeout(() => finishRound(), 700);
        }
      } else {
        syncBossHps();
      }
      return;
    }

    target.dead = true;
    audio.hit(target.def.difficulty);

    // Hit 'Em All: track uniqueness — duplicate zeroes the score.
    if (mode === "hit_em_all") {
      const id = target.def.id;
      if (uniqueHitRef.current.has(id)) {
        // duplicate — zero the score and flash
        setScore(0);
        scoreRef.current = 0;
        setScoreZeroed(true);
        setTimeout(() => setScoreZeroed(false), 700);
        audio.defeat();
        explode(x, y, 24, "#FF3B30");
        g.particles.push(new Particle(x, y - 20, "#FF3B30", {
          text: "DUPLICATE!", life: 50, gravity: 0.05, vy: -1.5, vx: 0,
        }));
        triggerShake(220, 10);
      } else {
        uniqueHitRef.current = new Set(uniqueHitRef.current).add(id);
        setUniqueHit(uniqueHitRef.current);
        audio.coin();
        const gained = 120;
        gameStore.addCoins(gained);
        setScore((s) => { scoreRef.current = s + gained; return s + gained; });
        setHits((h) => { hitsRef.current = h + 1; return h + 1; });
        g.particles.push(new Particle(x, y - 20, "#FFD700", {
          text: `+${gained}`, life: 45, gravity: 0.1, vy: -2, vx: 0,
        }));
        explode(x, y, 20, "#FFD700");
        // Win condition when all unique IDs collected
        if (uniqueHitRef.current.size >= UNIQUE_TARGET_IDS.length) {
          setTimeout(() => finishRound(), 500);
        }
      }
      return;
    }

    // Boomerang: hits on the return trip pay 3x.
    if (mode === "boomerang" && target.isReturning) {
      coin = coin * 3;
      g.particles.push(new Particle(x, y - 32, "#39FF14", {
        text: "RETURN x3!", life: 55, gravity: 0.04, vy: -2, vx: 0,
      }));
      explode(x, y, 22, "#39FF14");
    }

    // Bullseye Bonanza: hitting close to the target's center awards 5x coins.
    if (mode === "bullseye") {
      const dist = Math.hypot(x - target.x, y - target.y);
      if (dist <= (target.startSize || target.size) * 0.18) {
        coin = coin * 5;
        g.particles.push(new Particle(x, y - 32, "#FFD700", {
          text: "BULLSEYE!", life: 55, gravity: 0.04, vy: -2, vx: 0,
        }));
        explode(x, y, 26, "#FFD700");
        triggerShake(160, 8);
      }
    }

    // Shrinking Stars: smaller = bigger payout (up to 5x at the floor).
    if (mode === "shrinking_stars") {
      const start = target.startSize || target.size;
      const span = Math.max(1, start - (target.minSize || 18));
      const shrunkRatio = Math.max(0, Math.min(1, 1 - (target.size - (target.minSize || 18)) / span));
      const mult = 1 + shrunkRatio * 4;
      coin = Math.floor(coin * mult);
      if (mult >= 4) {
        g.particles.push(new Particle(x, y - 26, "#FF007F", {
          text: "JACKPOT!", life: 55, gravity: 0.04, vy: -2, vx: 0,
        }));
      }
    }

    // Conveyor Combo: hitting in left-to-right (or right-to-left) order grows
    // a multiplier; skipping ahead resets it.
    if (mode === "conveyor_combo" && target.isConveyor) {
      const skipped = g.targets.some((t) =>
        t !== target && t.isConveyor && !t.dead && t.lineId === target.lineId
        && ((target.vx > 0 && t.x < target.x) || (target.vx < 0 && t.x > target.x))
      );
      if (skipped) {
        conveyorMultRef.current = 1;
        setConveyorMult(1);
        g.particles.push(new Particle(x, y - 26, "#FF3B30", {
          text: "OUT OF ORDER!", life: 45, gravity: 0.05, vy: -1.6, vx: 0,
        }));
      } else {
        conveyorMultRef.current = Math.min(6, conveyorMultRef.current + 1);
        setConveyorMult(conveyorMultRef.current);
        g.particles.push(new Particle(x, y - 28, "#00FFFF", {
          text: `x${conveyorMultRef.current}`, life: 40, gravity: 0.05, vy: -1.6, vx: 0,
        }));
      }
      coin = Math.floor(coin * conveyorMultRef.current);
    }

    // Firework Frenzy: chain reaction — anything within RADIUS also pops.
    if (mode === "firework_frenzy") {
      const RADIUS = 130;
      const chain = g.targets.filter((t) =>
        t !== target && !t.dead && !t.isCoinPickup && !t.isPinata && !t.isBoss
        && t.def.difficulty !== "skull"
        && Math.hypot(t.x - x, t.y - y) <= RADIUS
      );
      if (chain.length > 0) {
        let chainCoins = 0;
        chain.forEach((t) => {
          t.dead = true;
          explode(t.x, t.y, 14, "#FF007F");
          explode(t.x, t.y, 8, "#FFD700");
          chainCoins += t.def.coin || 0;
        });
        gameStore.addCoins(chainCoins);
        scoreRef.current += chainCoins;
        hitsRef.current += chain.length;
        setScore(scoreRef.current);
        setHits(hitsRef.current);
        g.particles.push(new Particle(x, y - 34, "#FF007F", {
          text: `CHAIN x${chain.length + 1}!`, life: 55, gravity: 0.04, vy: -2, vx: 0,
        }));
        triggerShake(180, 9);
        audio.funny();
      }
    }

    audio.coin();
    // Slow-Mo Drop: hits during bullet time pay 2x.
    if (mode === "slowmo" && g.slowMoUntil && performance.now() < g.slowMoUntil) {
      coin = coin * 2;
    }
    gameStore.addCoins(coin);
    setScore((s) => { scoreRef.current = s + coin; return s + coin; });
    setHits((h) => { hitsRef.current = h + 1; return h + 1; });

    // Time Bandit: every successful hit extends the timer by 1 second.
    if (mode === "time_bandit") {
      setTimeLeft((t) => t + 1);
    }

    // Mirror Twins: track the most-recent hit per pairId on a frame-scoped
    // map. Hitting the partner within 1.5s fires a +200 bonus and clears the
    // entry so a third stale hit can't re-trigger it.
    if (mode === "mirror_twins" && target.isMirrorTwin && target.pairId) {
      g.twinHits = g.twinHits || new Map();
      const now = performance.now();
      const prev = g.twinHits.get(target.pairId);
      if (prev && now - prev < 1500) {
        g.twinHits.delete(target.pairId);
        const bonus = 200;
        gameStore.addCoins(bonus);
        scoreRef.current += bonus;
        setScore(scoreRef.current);
        audio.funny();
        explode(x, y, 28, "#00FFFF");
        explode(x, y, 18, "#FFD700");
        triggerShake(200, 10);
        g.particles.push(new Particle(x, y - 38, "#00FFFF", {
          text: `TWINS! +${bonus}`, life: 60, gravity: 0.04, vy: -2, vx: 0,
        }));
      } else {
        g.twinHits.set(target.pairId, now);
        g.particles.push(new Particle(x, y - 26, "#00FFFF", {
          text: "1 / 2", life: 35, gravity: 0.06, vy: -1.4, vx: 0,
        }));
      }
    }

    // Combo Frenzy: every successful hit grows the streak; every 5th hit fires
    // a celebratory bonus.
    if (mode === "combo_frenzy") {
      comboRef.current += 1;
      setCombo(comboRef.current);
      if (comboRef.current % 5 === 0) {
        const bonus = 200;
        gameStore.addCoins(bonus);
        scoreRef.current += bonus;
        setScore(scoreRef.current);
        audio.funny();
        explode(x, y, 30, "#FF6A00");
        explode(x, y, 18, "#FFD700");
        triggerShake(220, 11);
        g.particles.push(new Particle(x, y - 38, "#FF6A00", {
          text: `${comboRef.current} COMBO! +${bonus}`, life: 60, gravity: 0.04, vy: -2, vx: 0,
        }));
      } else {
        g.particles.push(new Particle(x, y - 32, "#FF6A00", {
          text: `${comboRef.current}x`, life: 30, gravity: 0.06, vy: -1.4, vx: 0,
        }));
      }
    }

    g.particles.push(new Particle(x, y - 20, "#FFD700", {
      text: `+${coin}`, life: 45, gravity: 0.1, vy: -2, vx: 0,
    }));
    explode(x, y, 16, "#FFD700");
    if (target.def.difficulty === "hard") {
      explode(x, y, 14, "#FF007F");
      triggerShake(80, 4);
    }
  }

  function explode(x, y, count, color) {
    const g = gameRef.current;
    for (let i = 0; i < count; i++) {
      g.particles.push(new Particle(x, y, color, {
        vx: (Math.random() - 0.5) * 8,
        vy: (Math.random() - 0.5) * 8 - 2,
        life: 35 + Math.random() * 15,
        size: 3 + Math.random() * 4,
      }));
    }
  }

  function drawCrosshair(ctx, x, y) {
    ctx.save();
    ctx.strokeStyle = "#00FFFF";
    ctx.shadowColor = "#00FFFF";
    ctx.shadowBlur = 12;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, 18, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 26, y); ctx.lineTo(x - 10, y);
    ctx.moveTo(x + 10, y); ctx.lineTo(x + 26, y);
    ctx.moveTo(x, y - 26); ctx.lineTo(x, y - 10);
    ctx.moveTo(x, y + 10); ctx.lineTo(x, y + 26);
    ctx.stroke();
    ctx.restore();
  }

  function drawCrossbow(ctx, g) {
    const cx = g.w / 2;
    const cy = g.h - 60 - (g.safeBottom || 0);
    const angle = Math.atan2(g.mouseY - cy, g.mouseX - cx);

    // Draw skin character behind the crossbow (not rotated)
    const skinId = state.equipped.skin;
    if (skinId) {
      const skinDef = SKINS.find((s) => s.id === skinId);
      const skinImg = ASSETS[skinId] ? getImage(ASSETS[skinId]) : null;
      const ss = 130;
      if (skinImg) {
        ctx.save();
        ctx.shadowColor = "#00FFFF";
        ctx.shadowBlur = 18;
        ctx.globalAlpha = 0.95;
        ctx.drawImage(skinImg, cx - ss / 2, cy - ss, ss, ss);
        ctx.restore();
      } else if (skinDef?.emoji) {
        ctx.save();
        ctx.shadowColor = "#00FFFF";
        ctx.shadowBlur = 18;
        ctx.font = `${Math.floor(ss * 0.85)}px serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(skinDef.emoji, cx, cy - ss / 2);
        ctx.restore();
      }
    }

    const crossbowId = state.equipped.crossbow;
    const crossbowDef = CROSSBOWS.find((c) => c.id === crossbowId);
    const img = ASSETS[crossbowId] ? getImage(ASSETS[crossbowId]) : null;
    const s = 130;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.shadowColor = "#00FFFF";
    ctx.shadowBlur = 18;
    if (img) {
      ctx.drawImage(img, -s / 2, -s / 2, s, s);
    } else if (crossbowDef?.emoji) {
      ctx.font = `${Math.floor(s * 0.75)}px serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(crossbowDef.emoji, 0, 0);
    } else {
      // fallback to wooden crossbow image
      const fb = getImage(ASSETS.crossbow_wooden);
      if (fb) ctx.drawImage(fb, -s / 2, -s / 2, s, s);
    }
    ctx.restore();
  }

  function shoot(clientX, clientY) {
    const g = gameRef.current;
    if (g.paused || g.ended) return;

    // Every mode consumes an arrow per shot. Out of arrows → no shot.
    if (arrowsLeftRef.current <= 0) {
      audio.miss();
      haptics.error();
      return;
    }
    arrowsLeftRef.current -= 1;
    setArrowsLeft(arrowsLeftRef.current);
    haptics.light();

    if (mode === "trophy_trial" && arrowsLeftRef.current === 0) {
      setTimeout(() => { if (!trophyHitRef.current) finishRound(); }, 1500);
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    g.mouseX = x; g.mouseY = y;

    const cx = g.w / 2;
    const cy = g.h - 60 - (g.safeBottom || 0);
    const dx = x - cx, dy = y - cy;
    const dist = Math.hypot(dx, dy) || 1;
    const speed = 18;
    const baseAngle = Math.atan2(dy, dx);

    const arrowDef = ARROWS.find((a) => a.id === state.equipped.arrow) || ARROWS[0];
    const rainbow = arrowDef.color === "rainbow";
    const color = rainbow ? "#FF007F" : arrowDef.color;
    // Triple Shot: every shot fires a 3-arrow fan for the same arrow cost.
    const angleOffsets = mode === "triple_shot" ? [-0.18, 0, 0.18] : [0];
    angleOffsets.forEach((offset) => {
      const a = baseAngle + offset;
      const arrow = new Arrow(cx, cy, Math.cos(a) * speed, Math.sin(a) * speed, color, arrowDef.glow, rainbow);
      // Bouncy Arrows: each arrow ricochets off the walls once.
      if (mode === "bouncy_arrows") arrow.bouncesLeft = 1;
      g.arrows.push(arrow);
    });
    audio.shoot();
  }

  function onPointerMove(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    gameRef.current.mouseX = e.clientX - rect.left;
    gameRef.current.mouseY = e.clientY - rect.top;
  }

  function onPointerDown(e) {
    // Pointer Events fire once per interaction regardless of input device,
    // so tapping on mobile no longer triggers both touchstart + synthetic
    // mousedown (the previous cause of double-shots).
    e.preventDefault();
    shoot(e.clientX, e.clientY);
  }

  function togglePause() {
    setPaused((p) => {
      gameRef.current.paused = !p;
      return !p;
    });
  }

  function openLeaveDialog() {
    pausedBeforeLeaveRef.current = gameRef.current.paused;
    if (!gameRef.current.paused) {
      gameRef.current.paused = true;
      setPaused(true);
    }
    setLeaveOpen(true);
  }

  function handleLeaveDialogChange(open) {
    setLeaveOpen(open);
    if (!open && !pausedBeforeLeaveRef.current) {
      gameRef.current.paused = false;
      setPaused(false);
    }
  }

  return (
    <div className="relative w-full h-full arcade-bg crosshair-zone" data-testid="play-screen">
      <div ref={wrapRef} className="absolute inset-0 game-wrap scanline">
        <canvas
          ref={canvasRef}
          className="block w-full h-full touch-none select-none"
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
          data-testid="game-canvas"
        />
      </div>

      <div className="absolute top-0 left-0 w-full flex justify-between items-start p-3 sm:p-4 z-10 pointer-events-none safe-top safe-x">
        <div className="flex flex-col gap-2">
          <button
            className="hud-pill hud-pill--cyan pointer-events-auto"
            onClick={() => { if (ended) nav("/"); else openLeaveDialog(); }}
            data-testid="play-back-btn"
          >
            <ArrowLeft className="w-4 h-4 text-[#00FFFF]" />
            <span className="text-[#00FFFF] font-display text-xs sm:text-sm">Home</span>
          </button>
          <div className="hud-pill hud-pill--gold">
            <Coins className="w-4 h-4 text-[#FFD700]" />
            <span className="neon-yellow text-sm sm:text-base" data-testid="play-score">{score}</span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="hud-pill hud-pill--pink">
            <Timer className="w-4 h-4 text-[#FF007F]" />
            <span className="neon-pink text-sm sm:text-base" data-testid="play-timer">{timeLeft}s</span>
          </div>
          {isMini && mode !== "boss" && mode !== "hit_em_all" && mode !== "trophy_trial" && !CHAIN_MODES.has(mode) && (
            <div className="hud-pill hud-pill--cyan text-xs">
              <TargetIcon className="w-3 h-3 text-[#00FFFF]" />
              <span className="neon-cyan">{hits}/{miniDef.hitGoal}</span>
            </div>
          )}
          {mode === "combo_frenzy" && (
            <div className="hud-pill hud-pill--pink text-xs" data-testid="combo-pill">
              <span>🔥</span>
              <span className="neon-pink">{combo} combo</span>
            </div>
          )}
          {mode === "conveyor_combo" && (
            <div className="hud-pill hud-pill--cyan text-xs" data-testid="conveyor-mult-pill">
              <span>🚂</span>
              <span className="neon-cyan">x{conveyorMult}</span>
            </div>
          )}
          {CHAIN_MODES.has(mode) && (
            <div className="hud-pill hud-pill--cyan text-xs" data-testid="chain-progress">
              <span>{mode === "chain_master" ? "⛓️" : "🔗"}</span>
              <span className="neon-cyan">{chainIndex}/{CHAIN_SEQUENCE.length}</span>
              {mode === "chain_master" && (
                <>
                  <span className="text-white/40">·</span>
                  <span style={{ color: "#B366FF" }}>
                    {chainHitsOnLink}/{CHAIN_MASTER_HITS_PER_LINK}
                  </span>
                </>
              )}
            </div>
          )}
          <div className="flex flex-col items-center gap-1">
            <div
              className={`hud-pill text-xs ${arrowsLeft <= ARROWS_LOW_THRESHOLD
                ? "hud-pill--pink anim-glow-pulse"
                : "hud-pill--cyan"}`}
              data-testid="arrows-pill"
              title="Arrows remaining"
            >
              <span>🏹</span>
              <span
                className={arrowsLeft <= ARROWS_LOW_THRESHOLD ? "neon-pink" : "neon-cyan"}
              >
                {arrowsLeft}
                {mode === "trophy_trial" ? " / 3" : ""}
              </span>
            </div>
            {arrowsLeft === 0 && refillIn !== null && (
              <div
                className="text-[10px] font-display uppercase tracking-wider text-[#FF007F] animate-pulse"
                data-testid="arrows-refill-countdown"
              >
                +{ARROWS_REPLENISH} in {refillIn}s
              </div>
            )}
          </div>
          {mode === "hit_em_all" && (
            <div className="flex flex-col items-center gap-1">
              <div
                className={`hud-pill hud-pill--pink text-xs ${scoreZeroed ? "anim-shake" : ""}`}
                data-testid="hitemall-counter"
                style={{ borderColor: scoreZeroed ? "#FF3B30" : undefined }}
              >
                <TargetIcon className="w-3 h-3" style={{ color: "#A020F0" }} />
                <span className="neon-pink">{uniqueHit.size}/{UNIQUE_TARGET_IDS.length} unique</span>
              </div>
              {scoreZeroed && (
                <div className="text-[10px] font-display uppercase tracking-wider" style={{ color: "#FF3B30" }}>
                  Duplicate! Score reset
                </div>
              )}
            </div>
          )}
          {mode === "boss" && bossHps.length > 0 && (
            <div className="flex flex-col items-center gap-2" data-testid="boss-hp-bars">
              <div className="hud-pill hud-pill--pink text-xs">
                <span className="neon-pink">
                  BOSSES LEFT · {bossHps.filter((b) => b.alive && b.hp > 0).length}/{bossesStartCountRef.current}
                </span>
              </div>
              {bossHps.map((b, i) => {
                const pct = Math.max(0, b.hp / b.max);
                const color = b.phase >= 3 ? "#FF3B30" : b.phase === 2 ? "#FFD700" : "#39FF14";
                return (
                  <div key={b.id} className="flex items-center gap-2">
                    <span
                      className="font-display text-[10px] uppercase tracking-wider w-14 text-right"
                      style={{ color: b.alive ? color : "#666" }}
                    >
                      Boss {i + 1}
                    </span>
                    <div className="w-36 h-2 rounded-full bg-black/50 border border-white/10 overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${pct * 100}%`,
                          background: b.alive ? color : "#333",
                          boxShadow: b.alive ? `0 0 10px ${color}` : undefined,
                        }}
                      />
                    </div>
                    <span className="text-[10px] text-white/70 font-display w-10">
                      {b.alive ? `${b.hp}/${b.max}` : "DOWN"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {mode === "classic" && (
            <div className="hud-pill hud-pill--cyan text-xs">
              <span className="neon-cyan">LVL {state.level}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 items-end">
          <button
            className="hud-pill hud-pill--cyan pointer-events-auto"
            onClick={togglePause}
            data-testid="pause-btn"
          >
            {paused
              ? <PlayIcon className="w-4 h-4 text-[#00FFFF]" />
              : <Pause className="w-4 h-4 text-[#00FFFF]" />}
            <span className="text-[#00FFFF] font-display text-xs sm:text-sm">
              {paused ? "Resume" : "Pause"}
            </span>
          </button>
        </div>
      </div>

      {isMini && (
        <div
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-center"
          style={{ bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.25rem))" }}
        >
          <div className="inline-block bg-black/50 backdrop-blur-md border-2 border-white/10 rounded-full px-5 py-2">
            <span
              className="font-display text-xs sm:text-sm uppercase tracking-wider"
              style={{ color: miniDef.color }}
            >
              {miniDef.icon} {miniDef.name}
            </span>
          </div>
        </div>
      )}

      {paused && !ended && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20">
          <div className="neon-card p-8 text-center">
            <h2 className="font-display font-black text-4xl neon-cyan mb-4">PAUSED</h2>
            <button className="btn-neon btn-pink" onClick={togglePause} data-testid="resume-btn">
              <PlayIcon className="w-5 h-5 fill-white" /> Resume
            </button>
          </div>
        </div>
      )}

      {ended && (
        <EndOverlay
          result={ended}
          mode={mode}
          score={score}
          hits={hits}
          miniDef={miniDef}
          streak={state.streak?.count || 1}
          onHome={() => nav("/")}
          onRetry={() => window.location.reload()}
        />
      )}

      <AlertDialog open={leaveOpen} onOpenChange={handleLeaveDialogChange}>
        <AlertDialogContent
          data-testid="leave-dialog"
          className="border-2 border-[#FF007F] shadow-[0_0_24px_rgba(255,0,127,0.4)] bg-[#14082e] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl neon-pink">
              Leave the game?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body text-white/80">
              Your current run will end and progress this round won&apos;t be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="leave-cancel-btn"
              className="btn-neon btn-cyan border-2"
            >
              Keep Playing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setLeaveOpen(false); nav("/"); }}
              data-testid="leave-confirm-btn"
              className="btn-neon btn-pink"
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
